import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { CustomFunctionEditor } from './CustomFunctionEditor';
import CustomReactPaginate from '../../../common/custom/CustomReactPaginate';

const CustomFunctionLogsTable = ({ projectId, customFunctionId, actions }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogIndex, setExpandedLogIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const perPage = 25;

  const fetchLogs = useCallback(
    async (page = currentPage) => {
      setLoading(true);
      try {
        const response = await actions.customFunctionLogs({
          projectId,
          customFunctionId,
          page,
          limit: perPage,
        });

        if (response.data && response.data.data && response.data.pagination) {
          setLogs(response.data.data || []);
          setTotalPages(response.data.pagination.totalPages || 0);
        } else if (response.data && Array.isArray(response.data)) {
          setLogs(response.data.reverse());
          setTotalPages(Math.ceil(response.data.length / perPage));
        } else {
          console.warn('Unexpected response structure:', response);
          setLogs([]);
          setTotalPages(0);
        }
        setExpandedLogIndex(null);
      } catch (error) {
        console.error('Error fetching logs:', error);
        setLogs([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [projectId, customFunctionId, actions, currentPage],
  );

  useEffect(() => {
    if (customFunctionId) {
      fetchLogs(currentPage);
    }
  }, [customFunctionId, currentPage, fetchLogs]);

  useEffect(() => {
    const handleRefresh = () => {
      setCurrentPage(1);
      fetchLogs(1);
    };

    document.addEventListener('refreshLogs', handleRefresh);
    return () => {
      document.removeEventListener('refreshLogs', handleRefresh);
    };
  }, [fetchLogs]);

  const toggleLog = (index) => {
    setExpandedLogIndex(expandedLogIndex === index ? null : index);
  };

  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1;
    setCurrentPage(newPage);
  };

  const renderLogsTable = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan="3" className="text-center">
            Loading logs...
          </td>
        </tr>
      );
    }

    if (!logs.length) {
      return (
        <tr>
          <td colSpan="3" className="text-center">
            No logs found.
          </td>
        </tr>
      );
    }

    return logs.flatMap((logItem, index) => {
      const uniqueKey = `${logItem.activationId || 'no-id'}-${index}`;

      return [
        <tr key={uniqueKey}>
          <td>{moment(logItem.timestamp).format('Do MMM YYYY hh:mm:ss A')}</td>
          <td>{logItem.activationId || 'N/A'}</td>
          <td>
            <button className="btn btn-xs btn-primary" onClick={() => toggleLog(index)}>
              {expandedLogIndex === index ? 'Hide Logs' : 'View Logs'}
            </button>
          </td>
        </tr>,
        expandedLogIndex === index && (
          <tr key={`${uniqueKey}-details`}>
            <td colSpan="3">
              <CustomFunctionEditor
                value={JSON.stringify(logItem.logs || [], null, 2)}
                mode="json"
              />
            </td>
          </tr>
        ),
      ];
    });
  };

  return (
    <>
      <div className="table-responsive">
        <table className="table table-bordered table-hover table-sm table-word-break connector-list">
          <thead>
            <tr>
              <th>Date</th>
              <th>Activation ID</th>
              <th>Logs</th>
            </tr>
          </thead>
          <tbody>{renderLogsTable()}</tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-3">
          <CustomReactPaginate
            totalPages={totalPages}
            page={currentPage - 1}
            onPageChange={handlePageChange}
            pageRangeDisplayed={5}
            marginPagesDisplayed={1}
          />
        </div>
      )}
    </>
  );
};

export default React.memo(CustomFunctionLogsTable);
